import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { bool, object as objectPropType } from "prop-types";
import cogoToast from "cogo-toast";
import EditCard from "../EditCard";
import CustomTextArea from "../../../../../components/CustomTextArea";
import Counter from "../../../../../components/Counter";
import {
  EDIT_PROFILE,
  PROFILE_LOADING,
  STOP_PROFILE_LOADING,
} from "../../../../../redux/actionTypes";
import { updateProfileAction } from "../../../../../redux/actions/profileActions";

const About = ({ profileData, profileLoading }) => {
  const MAX_WORDS = 200;

  const [text, setText] = useState(profileData.about || "");
  const [count, setCount] = useState(
    MAX_WORDS - (text.trim().split(/\s+/).length || 0)
  );

  const dispatch = useDispatch();

  const handleChange = ({ target: { id, value } }) => {
    const words = value.trim().split(/\s+/);
    const wordCount = words.length;

    if (wordCount >= MAX_WORDS && value.endsWith(" ")) {
      return;
    }

    if (wordCount <= MAX_WORDS) {
      setText(value);
      setCount(MAX_WORDS - wordCount);
      dispatch({ type: EDIT_PROFILE, payload: { name: id, value } });
    }
  };

  useEffect(() => {
    const aboutText = Array.isArray(profileData.about)
      ? profileData.about.join(" ")
      : profileData.about || "";
    setText(aboutText);
    setCount(MAX_WORDS - (aboutText.trim().split(/\s+/).length || 0));
  }, [profileData.about]);

  // Load parsed resume data from session storage if available
  // This effect checks sessionStorage when component mounts AND periodically
  useEffect(() => {
    let hasLoadedAbout = false;

    const checkAndLoadAboutData = () => {
      // Don't reload if we've already loaded data in this session
      if (hasLoadedAbout) return;

      const parsedDataStr = sessionStorage.getItem("parsedResumeData");
      if (parsedDataStr) {
        try {
          const parsedData = JSON.parse(parsedDataStr);
          console.log("About component - checking parsed data:", parsedData);

          if (parsedData.about) {
            console.log(
              "About component - found about data:",
              parsedData.about
            );

            // Limit to MAX_WORDS (200 words)
            const words = parsedData.about.trim().split(/\s+/);
            const limitedText = words.slice(0, MAX_WORDS).join(" ");

            setText(limitedText);
            setCount(MAX_WORDS - Math.min(words.length, MAX_WORDS));
            dispatch({
              type: EDIT_PROFILE,
              payload: { name: "about", value: limitedText },
            });

            // Mark as loaded
            hasLoadedAbout = true;

            // Show success message
            alert(
              "Professional summary loaded from resume! Please review and update as needed."
            );

            // Clear from session storage after loading
            sessionStorage.setItem(
              "parsedResumeData",
              JSON.stringify({
                ...parsedData,
                about: null,
              })
            );
          }
        } catch (error) {
          console.log("Error loading parsed about data:", error);
        }
      }
    };

    // Check immediately on mount
    checkAndLoadAboutData();

    // Also check every 500ms for 5 seconds in case user navigates to this page
    // after parsing (allows time for sessionStorage to be populated)
    const intervalId = setInterval(checkAndLoadAboutData, 500);

    // Clean up after 5 seconds
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
    }, 5000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [dispatch]);
  const handleSubmit = async () => {
    const payload = {
      first_name: profileData.first_name,
      last_name: profileData.last_name,
      gender: profileData.gender,
      dob: profileData.dob,
      state: profileData.state,
      country: profileData.country,
      industry: profileData.industry,
      image_url: profileData.image_url,
      resume: profileData.resume,
      phone: profileData.phone,
      links: profileData.links,
      isShowOnProfile: 1,
      about: text,
    };

    try {
      dispatch({ type: PROFILE_LOADING });

      await updateProfileAction(payload, dispatch);
      
      cogoToast.success("About section updated successfully!", {
        hideAfter: 4,
        position: "top-center",
      });
    } catch (error) {
      console.error("Error updating profile:", error);
      cogoToast.error(error?.message || "Failed to update about section", {
        hideAfter: 4,
        position: "top-center",
      });
    } finally {
      dispatch({ type: STOP_PROFILE_LOADING });
    }
  };

  return (
    <EditCard
      handleSubmit={handleSubmit}
      allRequired
      btnText="Update"
      fields
      loading={profileLoading}
    >
      <div className="w-100 position-relative" style={{ 
        width: '100%',
        marginBottom: '20px'
      }}>
        <div style={{
          position: 'absolute',
          top: '-10px',
          right: '10px',
          zIndex: 10
        }}>
          <Counter count={count} MAX_WORDS={MAX_WORDS} />
        </div>
        <CustomTextArea
          classes="pt-4"
          value={text}
          onChange={handleChange}
          rows="12"
          id="about"
          placeholder="Tell us about yourself... (200 words)"
          maxLength={MAX_WORDS * 6}
          style={{ 
            width: '100%',
            padding: '16px',
            fontSize: '16px',
            lineHeight: '1.5',
            border: '2px solid #e1e5e9',
            borderRadius: '12px',
            boxSizing: 'border-box',
            minHeight: '200px',
            resize: 'vertical',
            fontFamily: 'inherit',
            backgroundColor: '#fafbfc',
            transition: 'border-color 0.2s ease'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#3a6ed4';
            e.target.style.backgroundColor = '#fff';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e1e5e9';
            e.target.style.backgroundColor = '#fafbfc';
          }}
        />
        <div style={{
          marginTop: '12px',
          fontSize: '14px',
          color: '#6c757d',
          textAlign: 'center'
        }}>
          {count} words remaining
        </div>
      </div>
    </EditCard>
  );
};

About.propTypes = {
  profileData: objectPropType.isRequired,
  profileLoading: bool.isRequired,
};

export default About;
